import React, { Component } from 'react';
import SvgIcon from 'src/react_components/svg-icon';
import FeedbackIntroSection from 'annotator/feedback-intro-section';
import FeedbackCommentHelperSection from 'annotator/feedback-comment-helper-section';
import { AnnotatorObject, CommentCategory, CommentHelper } from 'annotator/annotator-types';

declare const Annotator: AnnotatorObject;
declare const _;
declare const $;

interface FeedbackSummaryAreaProps {
  // eslint-disable-next-line react/no-unused-prop-types
  key: CommentCategory
  category: CommentCategory
  categoryName: string
  content: string
  commentHelpers: CommentHelper[]
  deleteCommentHelper: (commentHelper: CommentHelper) => void
}

const FeedbackInfographicSection = ({ infographic, commentHelpers, deleteCommentHelper }) => (
  <div>
    {commentHelpers.map((commentHelper) => (
      <FeedbackCommentHelperSection
        commentHelper={commentHelper}
        key={commentHelper.id}
        deleteCommentHelper={deleteCommentHelper}
      />
    ))}
    <div>
      <img src={infographic.url} alt={infographic.altText} style={{ maxWidth: '100%', maxHeight: '120px' }} />
    </div>
  </div>
);

const FeedbackVideoSection = ({ video, commentHelpers, deleteCommentHelper }) => (
  <div className="d-flex p-3" style={{ backgroundColor: '#fafafa' }}>
    <div title={video.url} style={{ flexBasis: '60px', flexGrow: '0', flexShrink: '0' }}>
      <SvgIcon icon="ondemand_video" className="icon-xxl" />
    </div>
    <div className="flex-grow-1">
      {commentHelpers.map((commentHelper) => (
        <FeedbackCommentHelperSection
          commentHelper={commentHelper}
          key={commentHelper.timestamp}
          deleteCommentHelper={deleteCommentHelper}
        />
      ))}
    </div>
  </div>
);

const FeedbackSummarySection = ({
  type, infographic, video, commentHelper, commentHelpers, deleteCommentHelper
}) => {
  switch (type) {
    case 'comment-helper':
      return <FeedbackCommentHelperSection commentHelper={commentHelper} deleteCommentHelper={deleteCommentHelper} />;
    case 'infographic':
      return (
        <FeedbackInfographicSection
          infographic={infographic}
          commentHelpers={commentHelpers}
          deleteCommentHelper={deleteCommentHelper}
        />
      );
    case 'video':
      return (
        <FeedbackVideoSection
          video={video}
          commentHelpers={commentHelpers}
          deleteCommentHelper={deleteCommentHelper}
        />
      );
    default:
      return null;
  }
};

class FeedbackSummaryArea extends Component<FeedbackSummaryAreaProps> {
  state = {
    content: this.props.content,
    ajaxError: false
  };

  updateIntro = _.debounce((content) => {
    const { category } = this.props;
    const { content: prevContent } = this.state;

    if (content === prevContent) {
      return;
    }

    if (Annotator.sandbox) {
      this.updateIntroSuccess(content);
      return;
    }

    $.ajax({
      type: 'POST',
      url: Annotator.routes.feedbackSummaryAreas,
      data: {
        submission_uuid: Annotator.data.submissionUUID, category, content
      },
      success: () => {
        this.setState({ ajaxError: false });
        this.updateIntroSuccess(content);
      },
      error: (error) => {
        // A 409 (conflict) will only occur when there has been a duplicate request sent.
        // We don't want to alarm the specialist if/when that happens
        if (error.status !== 409) {
          this.setState({ ajaxError: true });
          Annotator.handleAjaxError(error, 'Unable to save feedback');
        }
      }
    });
  }, 500);

  updateIntroSuccess(content) {
    window.Annotator.checkIfReviewFinished();
    this.setState({ content });
  }

  render() {
    const {
      category, categoryName, commentHelpers, deleteCommentHelper
    } = this.props;

    const { content, ajaxError } = this.state;
    const { editable } = window.Annotator;

    const empty = $(`<div>${content || ''}</div>`).text().trim().length === 0 && !(commentHelpers || []).length;

    const groups = [];

    (commentHelpers || []).
      sort((a, b) => a.timestamp - b.timestamp).
      forEach((commentHelper) => {
        const { infographic, video } = commentHelper;

        if (infographic) {
          const { id } = infographic;
          let group = groups.find((g) => g.type === 'infographic' && g.id === id);
          if (!group) {
            group = {
              type: 'infographic', id, key: `i${id}`, infographic, commentHelpers: []
            };
            groups.push(group);
          }
          group.commentHelpers.push(commentHelper);
        } else if (video) {
          const { id } = video;
          let group = groups.find((g) => g.type === 'video' && g.id === id);
          if (!group) {
            group = {
              type: 'video', id, key: `v${id}`, video, commentHelpers: []
            };
            groups.push(group);
          }
          group.commentHelpers.push(commentHelper);
        } else {
          groups.push({
            key: `ch${commentHelper.id}`,
            type: 'comment-helper',
            commentHelper
          });
        }
      });

    const classes = `feedback-summary-area ${empty ? 'empty-category' : ''}`;
    const title = empty ? 'This category will not be included unless you add some content' : '';

    let renderedContent;
    if (editable) {
      renderedContent =
        <FeedbackIntroSection content={content} categoryName={categoryName} onChange={this.updateIntro} />;
    } else if (empty) {
      renderedContent = <div className="mx-3"><em>No feedback provided</em></div>;
    } else {
      renderedContent = /* eslint-disable-next-line react/no-danger */
        <div className="mx-3 feedback-summary-content" dangerouslySetInnerHTML={{ __html: content }} />;
    }

    return (
      <div className={classes} data-feedback-area-category={category}>
        <h4 title={title} className={ajaxError ? 'text-danger' : ''}>{categoryName}</h4>
        {renderedContent}
        {groups.map(({
          key, type, infographic, video, commentHelper, commentHelpers: commentHelperGroup
        }) => (
          <FeedbackSummarySection
            key={key}
            type={type}
            infographic={infographic}
            video={video}
            commentHelper={commentHelper}
            commentHelpers={commentHelperGroup}
            deleteCommentHelper={deleteCommentHelper}
          />
        ))}
      </div>
    );
  }
}

export default FeedbackSummaryArea;
